import React from 'react';
import { motion } from 'framer-motion';

const ButtonGroup = ({ label, options, value, onSelect, testIdPrefix }) => (
  <div>
    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2.5 block">{label}</label>
    <div className="flex flex-wrap gap-2">
      {options.map(({ key, label: optLabel }) => (
        <button
          key={key}
          data-testid={`${testIdPrefix}-${key}`}
          onClick={() => onSelect(key)}
          className={`px-4 py-2 rounded-full text-sm transition-all border ${
            value === key
              ? 'bg-[#235347] text-white border-[#235347]'
              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
          }`}
        >
          {optLabel}
        </button>
      ))}
    </div>
  </div>
);

const WORK_OPTIONS = [
  { key: 'full_time', label: 'Full-time' },
  { key: 'part_time', label: 'Part-time' },
  { key: 'self_employed', label: 'Self-employed' },
  { key: 'student', label: 'Student' },
  { key: 'not_working', label: 'Not working' },
  { key: 'retired', label: 'Retired' },
  { key: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const STRESS_OPTIONS = [
  { key: 'low', label: 'Low' },
  { key: 'moderate', label: 'Moderate' },
  { key: 'high', label: 'High' },
  { key: 'very_high', label: 'Very high' },
];

const SLEEP_OPTIONS = [
  { key: 'good', label: 'Good' },
  { key: 'variable', label: 'Variable' },
  { key: 'poor', label: 'Poor' },
  { key: 'very_poor', label: 'Very poor' },
];

const SUPPORT_OPTIONS = [
  { key: 'strong_support', label: 'Strong support network' },
  { key: 'some_support', label: 'Some support' },
  { key: 'limited_support', label: 'Limited support' },
  { key: 'isolated', label: 'Isolated' },
  { key: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export const OnboardingLifeContext = ({ data, onChange }) => {
  const ctx = data.life_context || {};

  const update = (field, value) => {
    onChange({ ...data, life_context: { ...ctx, [field]: value } });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-7">
      {/* Kindra message */}
      <div className="bg-white border border-slate-150 rounded-2xl p-5">
        <p className="text-slate-700 leading-relaxed">
          A few things about your day-to-day.
        </p>
        <p className="text-sm text-slate-400 mt-2">
          This helps me understand patterns and context — not judge.
        </p>
      </div>

      <ButtonGroup
        label="Work situation"
        options={WORK_OPTIONS}
        value={ctx.work}
        onSelect={(v) => update('work', v)}
        testIdPrefix="onboarding-work"
      />

      <ButtonGroup
        label="Stress level"
        options={STRESS_OPTIONS}
        value={ctx.stress_level}
        onSelect={(v) => update('stress_level', v)}
        testIdPrefix="onboarding-stress"
      />

      <ButtonGroup
        label="Sleep quality"
        options={SLEEP_OPTIONS}
        value={ctx.sleep_quality}
        onSelect={(v) => update('sleep_quality', v)}
        testIdPrefix="onboarding-sleep"
      />

      <ButtonGroup
        label="Support situation"
        options={SUPPORT_OPTIONS}
        value={ctx.support}
        onSelect={(v) => update('support', v)}
        testIdPrefix="onboarding-support"
      />

      <div>
        <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">Anything else?</label>
        <input
          data-testid="onboarding-life-notes"
          placeholder="Optional — e.g. recently moved, major life change"
          value={ctx.additional_notes || ''}
          onChange={(e) => update('additional_notes', e.target.value)}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20"
        />
      </div>
    </motion.div>
  );
};
